import React, { useState } from 'react';

import { find, get, xor, uniq } from 'lodash';
import { observer } from 'mobx-react-lite';
import { Form } from 'mobx-react-form';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Box,
  SxProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { RentRollUnit } from 'types/underwriting/income-approach';
import { RentLossItem } from 'types/underwriting/income-approach/cap-rate-conclusion';
import { formatCurrencyInt } from 'shared/utils/numberFormatters';
import { getRentLossPath } from '../helpers';
import { ConclusionType, RentLossType } from '../types';

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  textTransform: 'none',
  border: `1px solid ${theme.palette.primary.main}`,
  padding: theme.spacing(1, 3),
  fontWeight: 600,
  fontSize: '0.875rem',
  color: theme.palette.primary.main,
  backgroundColor: '#fff',
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:first-of-type': {
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
  },
  '&:last-of-type': {
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
  },
}));

const disabledRowStyles: SxProps = {
  backgroundColor: '#F8FAFC',
  color: '#617083',
  fontFamily: 'Nunito Sans',
  fontSize: 14,
  padding: '0px 8px 0px 16px',
  lineHeight: '36px',
};

const UNIT_TYPE_LABEL_MAP: Record<string, string> = {
  residential: 'Residential',
  commercial: 'Commercial',
};

type RentLossModalProps = {
  form: Form;
  isEditMode: boolean;
  index: number | null;
  type: RentLossType;
  valueAsComplete: boolean;
  valueAsStabilized: boolean;
  VCLossPercentage: number;
  units: RentRollUnit[];
  handleClosed: () => void;
  onAddRentLoss: (rentLossDataPath: string, units: RentRollUnit[], vcLossPercentage: number) => void;
  onEditRentLoss: (
    index: number,
    type: RentLossType,
    valueConclusion: ConclusionType,
    units: RentRollUnit[],
    vcLossPercentage: number
  ) => void;
};

const RentLossModal = observer(
  ({
    form,
    isEditMode,
    index,
    type,
    valueAsComplete,
    valueAsStabilized,
    VCLossPercentage,
    units,
    handleClosed,
    onAddRentLoss,
    onEditRentLoss,
  }: RentLossModalProps) => {
    const showValueConclusionToggle = valueAsComplete && valueAsStabilized && !isEditMode;
    const [valueConclusion, setValueConclusion] = useState<ConclusionType>(
      showValueConclusionToggle || valueAsStabilized ? 'asStabilized' : valueAsComplete ? 'asComplete' : 'asStabilized'
    );

    const initialSelectedUnits = isEditMode
      ? get(form.values(), `${getRentLossPath(type, valueConclusion)}.${index}.units`, [])
      : [];

    const [selectedUnits, setSelectedUnits] = useState<string[]>(initialSelectedUnits);

    const asStabilizedRentLossItems = get(form.values(), getRentLossPath(type, 'asStabilized'), []) as RentLossItem[];
    const asCompleteRentLossItems = get(form.values(), getRentLossPath(type, 'asComplete'), []) as RentLossItem[];

    const handleAddRentLoss = () => {
      const rentLossDataPath = getRentLossPath(type, valueConclusion);
      const targetUnits = units.filter((unit) => selectedUnits.includes(unit.id)) as RentRollUnit[];

      onAddRentLoss(rentLossDataPath, targetUnits, VCLossPercentage);

      handleClosed();
    };

    const handleUpdateRentLoss = () => {
      if (index == null) {
        return;
      }

      const targetUnits = units.filter((unit) => selectedUnits.includes(unit.id)) as RentRollUnit[];
      onEditRentLoss(index, type, valueConclusion, targetUnits, VCLossPercentage);

      handleClosed();
    };

    const handleUnitSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const unitId = find(event.target.attributes, (attribute) => attribute.name === 'id')?.value;

      if (!unitId || event.target.disabled) {
        return;
      }

      setSelectedUnits((prev) => {
        return checked ? [...prev, unitId] : prev.filter((selectedUnit) => selectedUnit !== unitId);
      });
    };

    const handleActionButtonClick = () => {
      if (isEditMode) {
        handleUpdateRentLoss();
      } else {
        handleAddRentLoss();
      }
    };

    const getUnitsInRentLoss = (items: RentLossItem[]) => {
      return uniq(items.flatMap((item) => item.units || []));
    };

    const preselectedUnits = getUnitsInRentLoss(
      valueConclusion === 'asStabilized' ? asStabilizedRentLossItems : asCompleteRentLossItems
    );

    const shouldDisableActionButton = isEditMode
      ? xor(selectedUnits, initialSelectedUnits).length === 0
      : selectedUnits.length === 0;

    return (
      <Dialog TransitionProps={{ onExited: handleClosed }} onClose={handleClosed} open maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit' : 'Add New'} {UNIT_TYPE_LABEL_MAP[type]} Rent Loss
        </DialogTitle>
        <DialogContent sx={{ flexGrow: 1, overflow: 'auto', paddingBottom: 0 }}>
          {showValueConclusionToggle && (
            <>
              <Typography sx={{ mb: 1 }} variant="body1">
                Select the value conclusion you would like to apply rent loss to.
              </Typography>
              <ToggleButtonGroup
                sx={{ mb: 2 }}
                value={valueConclusion}
                exclusive
                onChange={(_event, newValue) => setValueConclusion(newValue)}
                size="small"
                disabled={isEditMode}
              >
                <StyledToggleButton value="asStabilized">AS STABILIZED</StyledToggleButton>
                <StyledToggleButton value="asComplete">AS COMPLETE</StyledToggleButton>
              </ToggleButtonGroup>
            </>
          )}
          <Typography variant="body1">
            Select the <strong>{UNIT_TYPE_LABEL_MAP[type]}</strong> units you would like to apply rent loss to.
          </Typography>

          <Box sx={{ overflowY: 'auto', maxHeight: '60vh' }}>
            <Table stickyHeader>
              <TableHead>
                <TableCell>Unit Number</TableCell>
                {type === 'residential' && (
                  <>
                    <TableCell>Rooms</TableCell>
                    <TableCell># Bedrooms</TableCell>
                    <TableCell>Rent Type</TableCell>
                  </>
                )}
                {type === 'commercial' && (
                  <>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Use</TableCell>
                  </>
                )}
                <TableCell>Monthly Rent ($)</TableCell>
                <TableCell>Lease Status</TableCell>
                <TableCell>Rent Loss</TableCell>
              </TableHead>
              <TableBody>
                {units.map((unit) => {
                  const disabled = preselectedUnits.includes(unit.id) && !initialSelectedUnits.includes(unit.id);

                  return (
                    <TableRow sx={disabled ? disabledRowStyles : {}} key={`${type}-${unit.id}`}>
                      <TableCell>{unit.number}</TableCell>
                      {'bedrooms' in unit && (
                        <>
                          <TableCell>{(unit.bedrooms || 0) + 2}</TableCell>
                          <TableCell>{unit.bedrooms || 0}</TableCell>
                          <TableCell>{unit.rentType}</TableCell>
                        </>
                      )}
                      {'tenant' in unit && (
                        <>
                          <TableCell>{unit.tenant || 'N/A'}</TableCell>
                          <TableCell>{unit.use}</TableCell>
                        </>
                      )}
                      <TableCell>{formatCurrencyInt(unit.rent || 0)}</TableCell>
                      <TableCell>{unit.leaseStatus}</TableCell>
                      <TableCell>
                        <Checkbox
                          disabled={disabled}
                          checked={disabled || selectedUnits.includes(unit.id)}
                          id={unit.id}
                          onChange={handleUnitSelect}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          <DialogActions sx={{ borderTop: '1px solid #E2E8F0', paddingY: 2 }}>
            <div style={{ flex: 1 }} />
            <Button variant="outlined" onClick={handleClosed}>
              Cancel
            </Button>

            <Button variant="contained" disabled={shouldDisableActionButton} onClick={handleActionButtonClick}>
              {isEditMode ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    );
  }
);

export default RentLossModal;
